import { Checkbox, Input, Modal, Table, DatePicker, Select, Spin, message, Pagination } from 'antd';
import EditStudyDetailsModal from '../../components/EditStudyDetailsModal';
import SplitStudyModal from '../../components/SplitStudyModal';
import StudyMergeModal from '../../components/StudyMergeModal';
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { SavedSearches, orderColumns } from './constants';
import "./workstation.css";
import { getUserDetails, makePostCall } from '../../utils/helper';
import { useAllowedModalities } from '../../hooks/useAllowedModalities';
import dayjs from 'dayjs';
import FyzksInput from '../../components/FyzksInput';
import { debounce } from 'lodash';
import {sortedModalityOptionsNew} from '../../utils/helper';
import { maxTagTooltip } from '../../utils/selectHelpers';
import { REPORT_STATUS_OPTIONS } from '../../utils/shortcuts';
import { getPacsStatusLabel, getPatientIdLabel } from '../../constants/fieldLabels';

const { RangePicker } = DatePicker;

// The workstation used to be pinned to SCANNED server-side. Now that the API
// returns every status, default the filter to SCANNED so the default view is
// unchanged and the wider statuses (NEW / CANCELLED / SIGNEDOFF ...) are opt-in.
const DEFAULT_WS_FILTERS = { pacs_status: ['SCANNED'] };

const WorkstationList = ({ StyledTabsComponent }) => {
  const [orders, setOrders] = useState({ data: [], loading: true, total: 0 });
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [studyMergeVisible, setStudyMergeVisible] = useState(false);
  const [editStudyModalVisible, setEditStudyModalVisible] = useState(false);
  const [selectedStudyForEdit, setSelectedStudyForEdit] = useState(null);
  const [splitStudyModalVisible, setSplitStudyModalVisible] = useState(false);
  const [selectedStudyForSplit, setSelectedStudyForSplit] = useState(null);
  const [saveFiltersModal, setSaveFiltersModal] = useState({ visible: false, data: {} });
  const [filterName, setFilterName] = useState(null);
  const [filters, setFilters] = useState<any>({ ...DEFAULT_WS_FILTERS });
  const [savedFilters, setSavedFilters] = useState([]);
  const [quickFilters, setQuickFilters] = useState([]);
  const [modalityAllSelected, setModalityAllSelected] = useState(true);
  const userDetails = getUserDetails();
  const skipAutoFilterRunsRef = useRef(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
  });

  // Only the modalities this user is allowed to see (T001 c/d) — matches the
  // server-side filter now applied to /workstation-orders.
  const { modalityOptions } = useAllowedModalities();
  const today = dayjs();
  const yesterday = dayjs().subtract(1, 'days');
  const [dateRange, setDateRange] = useState([yesterday, today]);
  const sortedModalityOptions = sortedModalityOptionsNew(modalityOptions);

  // QC Workstation status filter. The list is wider than the PACS one because
  // /workstation-orders LEFT JOINs studies — orders that were never scanned
  // (ow_pacs_status = 'PENDING') show up here, so they need to be selectable.
  const workstationStatusOptions = [
    { label: getPacsStatusLabel('PENDING'), value: 'PENDING' },
    ...REPORT_STATUS_OPTIONS,
  ];

  useEffect(() => {
    if (skipAutoFilterRunsRef.current > 0) {
      skipAutoFilterRunsRef.current -= 1;
      return;
    }
    if (Object.keys(filters).length > 0 || dateRange) {
      setPagination({ current: 1, pageSize: pagination.pageSize });
    }
    debouncedFilter(filters);
  }, [filters]);

  useEffect(() => {
    if (skipAutoFilterRunsRef.current > 0) {
      skipAutoFilterRunsRef.current -= 1;
      return;
    }
    debouncedFilter(filters);
  }, [dateRange]);

  const debouncedFilter = useCallback(
    debounce((tempFilters) => {
      filterResults(tempFilters, 1, pagination.pageSize);
    }, 500),
    [dateRange, pagination.pageSize]
  );

  const handleEditStudyDetails = record => {
    setSelectedStudyForEdit(record);
    setEditStudyModalVisible(true);
  };

  const handleSplitStudy = record => {
    const studyUID = record?.pacs_study?.ps_study_uid;
    if (!studyUID) {
      message.error('Study UID not found');
      return;
    }
    // Adapt workstation record shape to match what SplitStudyModal expects
    const adapted = {
      ps_study_uid: studyUID,
      ps_study_dt_tm: record?.pacs_study?.ps_study_dt_tm,
      pacs_order: {
        pacs_ord_id: record?.pacs_ord_id,
        po_acc_no: record?.po_acc_no,
        patient: record?.patient,
      },
    };
    setSelectedStudyForSplit(adapted);
    setSplitStudyModalVisible(true);
  };

  const clearFilters = () => {
    skipAutoFilterRunsRef.current = 2;
    debouncedFilter.cancel();
    setSelectedRowKeys([]);
    setFilters({ ...DEFAULT_WS_FILTERS });
    setDateRange(null);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    filterResults({ ...DEFAULT_WS_FILTERS }, 1, pagination.pageSize, null);
  };

  const handleFilterChange = (key, value) => {
    const temp_filters = { ...filters };
    temp_filters[key] = value;
    setFilters(temp_filters);
  };

  const filterResults = (tempFilters, page = 1, pageSize = 20, overrideDateRange = undefined) => {
    setOrders({ ...orders, loading: true, data: [] });
    const payload = {
      ...tempFilters,
      page: page - 1,
      limit: pageSize,
    };
    const effectiveDateRange = overrideDateRange !== undefined ? overrideDateRange : dateRange;
    if (effectiveDateRange && Array.isArray(effectiveDateRange) && effectiveDateRange[0] && effectiveDateRange[1]) {
      payload.from_date = effectiveDateRange[0].format ? effectiveDateRange[0].format('YYYYMMDD') : effectiveDateRange[0];
      payload.to_date = effectiveDateRange[1].format ? effectiveDateRange[1].format('YYYYMMDD') : effectiveDateRange[1];
    }

    makePostCall('/workstation-orders', payload)
      .then(res => {
        setOrders({
          data: res.data?.data || [],
          loading: false,
          total: res.data?.total || 0,
        });
      })
      .catch(e => {
        console.error(e);
        setOrders({ data: [], loading: false, total: 0 });
      });
  };

  const handleStudyMergeClick = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Select a study to merge.');
      return;
    }
    if (selectedRowKeys.length > 1) {
      message.warning('Only one study can be selected at a time.');
      return;
    }
    setStudyMergeVisible(true);
  };

  const dataWithKeys = (orders.data || []).map(row => ({ ...row, key: row.pacs_ord_id }));
  const selectedStudyRecords = useMemo(
    () => dataWithKeys.filter(r => selectedRowKeys.includes(r.key)),
    [selectedRowKeys, dataWithKeys]
  );

  const columns = orderColumns({
    onEditStudyDetails: handleEditStudyDetails,
    onSplitStudy: handleSplitStudy,
  });

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>{StyledTabsComponent && StyledTabsComponent()}</div>
      </div>
      <div style={{ padding: '0 1.25rem' }}>
        <div className="ws-filters-card">
          <div className="filter-toolbar filter-toolbar--tech">
            <div className="filter-toolbar-main filter-toolbar-main--fields">
              <div className="ws-filters-row">
                <div className="ws-field ws-field--name">
                  <FyzksInput
                    size="small"
                    placeholder="Patient Name"
                    value={filters['pat_name'] || ''}
                    onChange={e => handleFilterChange('pat_name', e.target.value)}
                  />
                </div>

                <div className="ws-field ws-field--id">
                  <FyzksInput
                    size="small"
                    placeholder={getPatientIdLabel()}
                    value={filters['pat_pin'] || ''}
                    onChange={e => handleFilterChange('pat_pin', e.target.value)}
                  />
                </div>

                <div className="ws-field ws-field--acc">
                  <FyzksInput
                    size="small"
                    placeholder="Accession Number"
                    value={filters['po_acc_no'] || ''}
                    onChange={e => handleFilterChange('po_acc_no', e.target.value)}
                  />
                </div>

                <div className="ws-field ws-field--body">
                  <FyzksInput
                    size="small"
                    placeholder="Body Part / Study Desc"
                    value={filters['po_body_part'] || ''}
                    onChange={e => handleFilterChange('po_body_part', e.target.value)}
                  />
                </div>

                <div className="ws-field ws-field--ordno">
                  <FyzksInput
                    size="small"
                    placeholder="Order No"
                    value={filters['his_ord_no'] || ''}
                    onChange={e => handleFilterChange('his_ord_no', e.target.value)}
                  />
                </div>

                <div className="ws-field ws-field--status">
                  <Select
                    size="small"
                    mode="multiple"
                    allowClear
                    autoClearSearchValue={false}
                    maxTagCount={1}
                    className="single-line-tags"
                    maxTagPlaceholder={maxTagTooltip}
                    placeholder="Status"
                    value={filters['pacs_status'] || []}
                    style={{ width: '100%' }}
                    options={workstationStatusOptions}
                    optionRender={(option) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Checkbox checked={filters['pacs_status']?.includes(option.value)} style={{ pointerEvents: 'none' }} />
                        <span>{option.label}</span>
                      </div>
                    )}
                    onChange={val => handleFilterChange('pacs_status', val)}
                  />
                </div>

                <div className="ws-field ws-field--date">
                  <RangePicker
                    size="small"
                    style={{ width: '100%' }}
                    value={dateRange}
                    onChange={val => {
                      if (val) {
                        setDateRange([val[0], val[1]]);
                      } else {
                        setDateRange(null);
                      }
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="filter-toolbar-side">
              <div className="ws-filter-actions">
                <button className="btn-primary btn-compact" onClick={() => filterResults(filters)}>
                  Search
                </button>
                <button className="btn-secondary btn-compact" onClick={clearFilters}>
                  Clear Search
                </button>
                {(() => {
                  const selectedRecord = selectedStudyRecords[0];
                  const pacsStatus = selectedRecord?.order_workflow?.ow_pacs_status?.toUpperCase();
                  const isMergeableStatus = pacsStatus === 'SCANNED';
                  const isDisabled = selectedRowKeys.length !== 1 || !isMergeableStatus;

                  let tooltipTitle = '';
                  if (selectedRowKeys.length === 0) tooltipTitle = 'Select a study';
                  else if (selectedRowKeys.length > 1) tooltipTitle = 'Only one study can be selected';
                  else if (!isMergeableStatus) tooltipTitle = 'Study Merge is only allowed for Scanned studies';
                  return (
                    <button
                      className="btn-primary btn-compact"
                      onClick={handleStudyMergeClick}
                      disabled={isDisabled}
                      title={tooltipTitle}
                    >
                      Study Merge
                    </button>
                  );
                })()}
              </div>

              <div className="filter-layout-modalities filter-layout-modalities--solo">
                <div className="filter-modalities-header">
                  <Checkbox
                    className="filter-modalities-selectall"
                    checked={modalityAllSelected}
                    indeterminate={!modalityAllSelected && (filters['modality']?.length > 0)}
                    onChange={e => {
                      setModalityAllSelected(e.target.checked);
                      handleFilterChange('modality', []);
                    }}
                  >
                    Select all
                  </Checkbox>
                </div>
                <Checkbox.Group
                  value={modalityAllSelected ? sortedModalityOptions.map(o => o.value) : (filters['modality'] || [])}
                  onChange={vals => {
                    const allChecked = vals.length === sortedModalityOptions.length;
                    setModalityAllSelected(allChecked);
                    handleFilterChange('modality', allChecked ? [] : vals);
                  }}
                >
                  {sortedModalityOptions.map(opt => (
                    <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
                  ))}
                </Checkbox.Group>
              </div>
            </div>
          </div>
        </div>
        </div>
        <div className="orders-list">
          <Table
            className="workstation-table"
            tableLayout="fixed"
            loading={orders.loading}
            columns={columns as any}
            dataSource={dataWithKeys}
            rowKey="key"
            rowClassName={(record, index) => {
              const zebra = index % 2 === 0 ? 'ws-row-odd' : 'ws-row-even';
              // Priority tints mirror the AG Grid tables (PACS / Worklist / Orders):
              // EMERGENCY = red, ASAP = amber. EMERGENCY wins if both apply.
              const priority = record?.po_priority?.toUpperCase();
              if (priority === 'EMERGENCY') return `${zebra} ws-emergency-row`;
              if (priority === 'ASAP') return `${zebra} ws-asap-row`;
              return zebra;
            }}
            rowSelection={{
              selectedRowKeys,
              onChange: keys => {
                // Allow only one selection
                setSelectedRowKeys(keys.length > 1 ? [keys[keys.length - 1]] : keys);
              },
            }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: orders?.total || 0,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize });
                filterResults(filters, page, pageSize);
              },
              showTotal: (total: any, range: any) => `Showing ${range[0]}-${range[1]} of ${total} items`
            }}
            style={{ width: '100%' }}
            scroll={{
              x: 'max-content',
              y: 'calc(100vh - 27rem)',
            }}
          />
        </div>

        <StudyMergeModal
          visible={studyMergeVisible}
          selectedStudies={selectedStudyRecords}
          onCancel={() => setStudyMergeVisible(false)}
          onSuccess={() => {
            setStudyMergeVisible(false);
            setSelectedRowKeys([]);
            filterResults(filters, pagination.current, pagination.pageSize);
          }}
        />

        {selectedStudyForEdit && (
          <EditStudyDetailsModal
            visible={editStudyModalVisible}
            onCancel={() => {
              setEditStudyModalVisible(false);
              setSelectedStudyForEdit(null);
            }}
            allowPatientIdEdit={userDetails?.user_type === 'technician'}
            onSuccess={() => {
              filterResults(filters, pagination.current, pagination.pageSize);
            }}
            studyData={{
              patientName: selectedStudyForEdit?.patient?.pat_name || '',
              patientId: selectedStudyForEdit?.patient?.pat_pin || '',
              patientAge: selectedStudyForEdit?.patient?.pat_age || '',
              patientDOB: selectedStudyForEdit?.patient?.pat_dob,
              patientGender: selectedStudyForEdit?.patient?.pat_sex || '',
              studyDescription: selectedStudyForEdit?.po_diag_desc || '',
              accessionNumber: selectedStudyForEdit?.po_acc_no || '',
              referringPhysician: selectedStudyForEdit?.po_ref_doc || '',
              history: selectedStudyForEdit?.po_history || '',
              bodyPart: selectedStudyForEdit?.po_body_part || '',
              studyUID: selectedStudyForEdit?.pacs_study?.ps_study_uid,
              orderId: selectedStudyForEdit?.pacs_ord_id,
            }}
          />
        )}

        {selectedStudyForSplit && (
          <SplitStudyModal
            visible={splitStudyModalVisible}
            record={selectedStudyForSplit}
            onCancel={() => {
              setSplitStudyModalVisible(false);
              setSelectedStudyForSplit(null);
            }}
            onSuccess={() => {
              filterResults(filters, pagination.current, pagination.pageSize);
            }}
          />
        )}
      </div>
  );
}

export default WorkstationList;
